import { computed, readonly, ref } from 'vue';

import { useStorage } from '@peanuds/composables';

export type ColorScheme = 'light' | 'dark';
export type ColorSchemePreference = ColorScheme | 'system';

/** Shared with the pre-paint snippet in `index.html`; changing one breaks the other. */
export const COLOR_SCHEME_STORAGE_KEY = 'color-scheme';

const isColorScheme = (value: unknown): value is ColorScheme => {
  return value === 'light' || value === 'dark';
};

const isColorSchemePreference = (value: unknown): value is ColorSchemePreference => {
  return isColorScheme(value) || value === 'system';
};

const systemDarkQuery = () => window.matchMedia?.('(prefers-color-scheme: dark)');

const prefersDark = (): boolean => {
  return systemDarkQuery()?.matches === true;
};

const resolveScheme = (preference: ColorSchemePreference): ColorScheme => {
  if (preference !== 'system') {
    return preference;
  }

  return prefersDark() ? 'dark' : 'light';
};

const applyToDocument = (scheme: ColorScheme): void => {
  // Light is the bare `:root` palette, so the attribute is removed rather than
  // set to "light" — see theme-vars.scss.
  if (scheme === 'dark') {
    document.documentElement.dataset.theme = 'dark';
    return;
  }

  delete document.documentElement.dataset.theme;
};

// Module scope, so every caller drives the one <html data-theme> instead of
// holding a copy that can drift out of step with it.
const colorScheme = ref<ColorScheme>('light');
const colorSchemePreference = ref<ColorSchemePreference>('system');
let hasResolvedInitialScheme = false;

const followSystemScheme = (event: MediaQueryListEvent): void => {
  if (colorSchemePreference.value !== 'system') {
    return;
  }

  colorScheme.value = event.matches ? 'dark' : 'light';
  applyToDocument(colorScheme.value);
};

export const useColorScheme = () => {
  const storage = useStorage<ColorSchemePreference>(COLOR_SCHEME_STORAGE_KEY, {
    storage: 'local',
  });

  if (!hasResolvedInitialScheme) {
    const stored = storage.get();
    colorSchemePreference.value = isColorSchemePreference(stored) ? stored : 'system';

    // The pre-paint snippet already resolved this and stamped the document, so
    // trust the attribute first; the rest covers a page that skipped it.
    const stamped = document.documentElement.dataset.theme;
    colorScheme.value = isColorScheme(stamped)
      ? stamped
      : resolveScheme(colorSchemePreference.value);

    applyToDocument(colorScheme.value);
    systemDarkQuery()?.addEventListener?.('change', followSystemScheme);
    hasResolvedInitialScheme = true;
  }

  const isDark = computed(() => colorScheme.value === 'dark');

  const setColorScheme = (preference: ColorSchemePreference): void => {
    colorSchemePreference.value = preference;
    colorScheme.value = resolveScheme(preference);
    storage.set(preference);
    applyToDocument(colorScheme.value);
  };

  const toggleColorScheme = (): void => {
    setColorScheme(isDark.value ? 'light' : 'dark');
  };

  return {
    colorScheme: readonly(colorScheme),
    colorSchemePreference: readonly(colorSchemePreference),
    isDark,
    setColorScheme,
    toggleColorScheme,
  };
};
